import { Vector3 } from 'three';
import { type BrushFace, srcDirToWorld } from './faces';
import {
  type BrushSide,
  type Plane,
  RIDE_CONTENTS,
  readBrushes,
  readBrushSides,
  readPlanes,
} from './lumps';
import type { BspReader } from './reader';
import { srcToWorld } from './transform';

/**
 * Brush → collision geometry. A Source brush is constructive solid geometry: a
 * convex volume defined by the half-spaces of its side planes (a point is inside
 * when `n·p ≤ dist` for every side). To get triangles we reverse the compile:
 * for each side, start with a huge quad on its plane and clip it by every other
 * side plane; the survivor is that face's polygon. This yields *exactly* the
 * surfaces Source player physics rides (incl. invisible `playerclip`), so the
 * converted ramps surf faithfully. Reference: the classic "expand planes to
 * windings" algorithm used by map compilers / bspsrc.
 */

/** Half-extent of the seed quad — bigger than any Source map (±16384). */
const SEED = 1 << 17;
const CLIP_EPS = 0.1; // inches

/** Seed quad lying on plane (n, dist), wound CCW about n, in Source coords. */
function seedQuad(n: Vector3, dist: number): Vector3[] {
  const center = n.clone().multiplyScalar(dist);
  const ref = Math.abs(n.x) < 0.9 ? new Vector3(1, 0, 0) : new Vector3(0, 1, 0);
  const u = ref
    .clone()
    .sub(n.clone().multiplyScalar(ref.dot(n)))
    .normalize();
  const v = new Vector3().crossVectors(n, u); // CCW about n
  return [
    center.clone().addScaledVector(u, SEED).addScaledVector(v, SEED),
    center.clone().addScaledVector(u, -SEED).addScaledVector(v, SEED),
    center.clone().addScaledVector(u, -SEED).addScaledVector(v, -SEED),
    center.clone().addScaledVector(u, SEED).addScaledVector(v, -SEED),
  ];
}

/** Sutherland–Hodgman: keep the part of `poly` inside half-space n·p ≤ dist. */
function clip(poly: Vector3[], n: Vector3, dist: number): Vector3[] {
  const out: Vector3[] = [];
  for (let i = 0; i < poly.length; i++) {
    const a = poly[i] as Vector3;
    const b = poly[(i + 1) % poly.length] as Vector3;
    const da = n.dot(a) - dist;
    const db = n.dot(b) - dist;
    if (da <= CLIP_EPS) out.push(a);
    if ((da < -CLIP_EPS && db > CLIP_EPS) || (da > CLIP_EPS && db < -CLIP_EPS)) {
      const t = da / (da - db);
      out.push(a.clone().lerp(b, t));
    }
  }
  return out;
}

/** Build the world-space face polygons of one brush from its side planes. */
export function brushToWorldFaces(planes: Plane[], sides: BrushSide[]): BrushFace[] {
  const faces: BrushFace[] = [];
  for (const side of sides) {
    if (side.bevel) continue; // bevels are extra clip planes, not real faces
    const p = planes[side.planenum];
    if (!p) continue;
    const n = new Vector3(p.nx, p.ny, p.nz);
    const len = n.length();
    if (len < 1e-6) continue;
    n.divideScalar(len);
    const dist = p.dist / len;

    let poly = seedQuad(n, dist);
    for (const other of sides) {
      if (other === side) continue;
      const q = planes[other.planenum];
      if (!q) continue;
      const qn = new Vector3(q.nx, q.ny, q.nz);
      const ql = qn.length();
      if (ql < 1e-6) continue;
      poly = clip(poly, qn.divideScalar(ql), q.dist / ql);
      if (poly.length < 3) break;
    }
    if (poly.length < 3) continue;

    faces.push({
      verts: poly.map((v) => srcToWorld(v.x, v.y, v.z)),
      normal: srcDirToWorld(n.x, n.y, n.z),
    });
  }
  return faces;
}

/** Every ride-surface brush in the BSP as world-space polygons. */
export function buildBrushFaces(reader: BspReader): BrushFace[] {
  const planes = readPlanes(reader);
  const brushes = readBrushes(reader);
  const sides = readBrushSides(reader);
  const out: BrushFace[] = [];
  for (const brush of brushes) {
    if ((brush.contents & RIDE_CONTENTS) === 0) continue;
    out.push(
      ...brushToWorldFaces(planes, sides.slice(brush.firstside, brush.firstside + brush.numsides)),
    );
  }
  return out;
}
